/**
 * Desk phones: the models Stitch can draw, what their programmable keys can do, and the key
 * layout as the portal stores it. PBX-neutral: the Vodia adapter maps this to its own button
 * list (vodia69/phones.ts).
 *
 * Key counts come from Yealink datasheets as re-published by distributors (see
 * docs/phones.md). Which side of the screen each key sits on is confirmed for some models only;
 * `layoutConfirmed` says which, so the picture can be checked against a real phone.
 */

export type PhoneFamily = 'T3' | 'T4' | 'T5' | 'T7' | 'T8' | 'conference';

export interface ExpansionModule {
  name: string;
  /** Keys on one page of the module. */
  perPage: number;
  pages: number;
  /** Modules one phone takes. */
  max: number;
}

export interface PhoneModel {
  /** Stitch's id: "yealink-t54w". */
  id: string;
  vendor: string;
  /** As the phone system's model list names it. */
  model: string;
  family: PhoneFamily;
  screen: { inches: number; color: boolean; touch: boolean };
  /**
   * Programmable keys beside the screen (left column, right column), or on the screen itself
   * for touch models (shown two columns at a time).
   */
  keys: { left: number; right: number; touch?: false } | { onScreen: number; touch: true };
  /** Pages of keys. On T3/T4/T5 phones the last key of a page turns it when there is more than one. */
  pages: number;
  /** Every programmable key across pages. */
  lineKeys: number;
  /** The last physical key turns pages (T3/T4/T5), rather than a separate page button. */
  pageKey: boolean;
  softkeys: number;
  expansion?: ExpansionModule;
  /** The left/right split comes from the vendor's text, not from photos. */
  layoutConfirmed: boolean;
  /** No longer sold; still supported. */
  endOfSale?: boolean;
}

const EXP50: ExpansionModule = { name: 'EXP50', perPage: 20, pages: 3, max: 3 };
const EXP43: ExpansionModule = { name: 'EXP43', perPage: 20, pages: 3, max: 3 };
const EXP40: ExpansionModule = { name: 'EXP40', perPage: 20, pages: 2, max: 6 };
const EXP55: ExpansionModule = { name: 'EXP55', perPage: 26, pages: 3, max: 3 };

const y = (
  model: string,
  family: PhoneFamily,
  screen: PhoneModel['screen'],
  keys: PhoneModel['keys'],
  pages: number,
  lineKeys: number,
  rest: Partial<Pick<PhoneModel, 'expansion' | 'layoutConfirmed' | 'endOfSale' | 'softkeys' | 'pageKey'>> = {}
): PhoneModel => ({
  id: `yealink-${model.toLowerCase()}`,
  vendor: 'Yealink',
  model,
  family,
  screen,
  keys,
  pages,
  lineKeys,
  pageKey: rest.pageKey ?? (family === 'T3' || family === 'T4' || family === 'T5'),
  softkeys: rest.softkeys ?? 4,
  ...(rest.expansion ? { expansion: rest.expansion } : {}),
  layoutConfirmed: rest.layoutConfirmed ?? false,
  ...(rest.endOfSale ? { endOfSale: true } : {})
});

const mono = (inches: number) => ({ inches, color: false, touch: false });
const color = (inches: number) => ({ inches, color: true, touch: false });
const touch = (inches: number) => ({ inches, color: true, touch: true });

/** Most-used first within each series. */
export const PHONE_MODELS: readonly PhoneModel[] = [
  // T5: the current business line.
  y('T54W', 'T5', color(4.3), { left: 5, right: 5 }, 3, 27, { expansion: EXP50 }),
  y('T53W', 'T5', mono(3.7), { left: 4, right: 4 }, 3, 21, { expansion: EXP50 }),
  y('T53', 'T5', mono(3.7), { left: 4, right: 4 }, 3, 21, { expansion: EXP50 }),
  y('T57W', 'T5', touch(7), { onScreen: 29, touch: true }, 1, 29, { expansion: EXP50, layoutConfirmed: true }),
  y('T58W', 'T5', touch(7), { onScreen: 9, touch: true }, 3, 27, { expansion: EXP50, layoutConfirmed: true, pageKey: false }),
  // T3: entry level.
  y('T31G', 'T3', mono(2.3), { left: 0, right: 2 }, 1, 2, { layoutConfirmed: true }),
  y('T31P', 'T3', mono(2.3), { left: 0, right: 2 }, 1, 2, { layoutConfirmed: true }),
  y('T33G', 'T3', color(2.4), { left: 0, right: 4 }, 4, 12, { layoutConfirmed: true }),
  y('T34W', 'T3', color(2.4), { left: 0, right: 4 }, 4, 12),
  // T4U.
  y('T46U', 'T4', color(4.3), { left: 5, right: 5 }, 3, 27, { expansion: EXP43 }),
  y('T44U', 'T4', color(2.8), { left: 4, right: 4 }, 3, 21, { expansion: EXP43 }),
  y('T44W', 'T4', color(2.8), { left: 4, right: 4 }, 3, 21, { expansion: EXP43 }),
  y('T43U', 'T4', mono(3.7), { left: 4, right: 4 }, 3, 21, { expansion: EXP43, endOfSale: true }),
  y('T48U', 'T4', touch(7), { onScreen: 29, touch: true }, 1, 29, { expansion: EXP43, layoutConfirmed: true }),
  // T4S: older, still common.
  y('T46S', 'T4', color(4.3), { left: 5, right: 5 }, 3, 27, { expansion: EXP40 }),
  y('T42S', 'T4', mono(2.7), { left: 3, right: 3 }, 3, 15),
  y('T41S', 'T4', mono(2.7), { left: 3, right: 3 }, 3, 15),
  y('T48S', 'T4', touch(7), { onScreen: 29, touch: true }, 1, 29, { expansion: EXP40, layoutConfirmed: true }),
  // T7 and T8: no key is given up to turn pages.
  y('T74U', 'T7', color(4.3), { left: 5, right: 5 }, 5, 50, { expansion: EXP55 }),
  y('T73U', 'T7', color(2.8), { left: 4, right: 4 }, 5, 40, { expansion: EXP55 }),
  // The 7" touch models have 84 keys, 11 on the screen at a time (8 pages here: 7 of 11, one of
  // 7). Until 1.12.20 Stitch had them as 11 keys in all, so a T87W with 14 keys set couldn't be
  // changed at all (Oct 28, 2026). Sources: Yealink's own datasheets as distributors republish
  // them: SIP-T87W "84 one-touch DSS keys" and "up to 84 programmable buttons"; SIP-T88W "84
  // one-touch DSS keys"; SIP-T77U "84 one-touch DSS keys" and up to three expansion modules "for
  // up to 234 additional buttons" (3 EXP55 of 78). The phone system agrees: 69.5.22's button
  // list for a T87W has linekey.1 to linekey.84 plus 3 x 78 expansion keys (admin GUI HAR, Oct 28
  // 2026). Where Stitch has read a phone's button list, that list decides which keys exist.
  y('T77U', 'T7', touch(7), { onScreen: 11, touch: true }, 8, 84, { expansion: EXP55, layoutConfirmed: true }),
  y('T85W', 'T8', color(5.5), { left: 6, right: 6 }, 5, 60, { expansion: EXP55 }),
  y('T87W', 'T8', touch(7), { onScreen: 11, touch: true }, 8, 84, { expansion: EXP55, layoutConfirmed: true }),
  y('T88W', 'T8', touch(7), { onScreen: 11, touch: true }, 8, 84, { expansion: EXP55, layoutConfirmed: true }),
  // Conference phones: no programmable keys.
  y('CP920', 'conference', mono(3.1), { left: 0, right: 0 }, 1, 0, { softkeys: 0, layoutConfirmed: true, pageKey: false }),
  y('CP925', 'conference', touch(4), { onScreen: 0, touch: true }, 1, 0, { softkeys: 0, layoutConfirmed: true, pageKey: false })
];

const BY_ID = new Map(PHONE_MODELS.map((m) => [m.id, m]));

export function phoneModel(id: string): PhoneModel | undefined {
  return BY_ID.get(id);
}

/** The model the phone system names ("Yealink", "SIP-T54W" or "T54W"), if Stitch knows it. */
export function modelFromPbx(vendor: string, model: string): PhoneModel | undefined {
  const v = vendor.trim().toLowerCase();
  const m = model.trim().toUpperCase().replace(/^SIP-/, '');
  return PHONE_MODELS.find((p) => p.vendor.toLowerCase() === v && p.model.toUpperCase() === m);
}

/** Models offered when adding a phone: current ones first. */
export const PHONE_MODEL_OPTIONS = PHONE_MODELS.map((m) => ({ value: m.id, label: `${m.vendor} ${m.model}`, description: describeModel(m) }));

export function describeModel(m: PhoneModel): string {
  if (m.lineKeys === 0) return 'Conference phone';
  const screen = `${m.screen.inches}″ ${m.screen.touch ? 'touch screen' : m.screen.color ? 'color screen' : 'screen'}`;
  return `${screen}, ${m.lineKeys} programmable key${m.lineKeys === 1 ? '' : 's'}${m.endOfSale ? ' · no longer sold' : ''}`;
}

// --- MAC addresses --------------------------------------------------------------------------

/** "80:5e:c0:12:34:56", "805E-C012-3456" → "805EC0123456". Null when it isn't a MAC address. */
export function normalizeMac(input: string): string | null {
  const hex = input
    .trim()
    .replace(/[\s:.-]/g, '')
    .toUpperCase();
  if (!/^[0-9A-F]{12}$/.test(hex)) return null;
  if (hex === '000000000000' || hex === 'FFFFFFFFFFFF') return null;
  return hex;
}

/** "805EC0123456" → "80:5E:C0:12:34:56". */
export function formatMac(mac: string): string {
  return mac.match(/.{2}/g)?.join(':') ?? mac;
}

/** Yealink's own MAC prefixes, to suggest the vendor when someone types a MAC address. */
const YEALINK_OUIS = ['001565', '805EC0', '805E0C', '249AD8', '44DBD2', 'C4FC22'];
export function looksYealink(mac: string): boolean {
  return YEALINK_OUIS.some((o) => mac.toUpperCase().startsWith(o));
}

// --- Keys -------------------------------------------------------------------------------------

/** What a key's target must be. */
export type KeyTarget = 'person' | 'number' | 'callPark' | 'callQueue' | 'ringGroup' | 'timeRule' | 'autoAttendant' | 'optionalNumber' | 'url' | 'digits';

export interface KeyType {
  value: string;
  label: string;
  description: string;
  group: 'Calls' | 'Call routing' | 'Phone' | 'Advanced';
  target?: KeyTarget;
  /** Short word shown on the key when no label is set. */
  short: string;
}

export const KEY_TYPES: readonly KeyType[] = [
  { value: 'line', label: 'Line', description: 'Your own line: make and answer calls.', group: 'Calls', short: 'Line' },
  {
    value: 'person',
    label: 'Person',
    description: 'Lights up when they’re on a call. Press to call them, or to pick up their ringing call.',
    group: 'Calls',
    target: 'person',
    short: 'Person'
  },
  { value: 'speedDial', label: 'Speed dial', description: 'Calls a number with one press.', group: 'Calls', target: 'number', short: 'Speed dial' },
  { value: 'intercom', label: 'Intercom', description: 'Calls someone and opens their speaker straight away.', group: 'Calls', target: 'person', short: 'Intercom' },
  { value: 'park', label: 'Park', description: 'Parks a call to pick up from another phone. Lights up while a call is parked.', group: 'Calls', target: 'callPark', short: 'Park' },
  { value: 'page', label: 'Paging', description: 'Announces over a group of phones.', group: 'Calls', target: 'number', short: 'Page' },
  { value: 'voicemail', label: 'Voicemail', description: 'Listens to messages.', group: 'Calls', target: 'optionalNumber', short: 'Voicemail' },
  { value: 'queue', label: 'Queue sign in', description: 'Signs in to, or out of, a call queue.', group: 'Call routing', target: 'callQueue', short: 'Queue' },
  { value: 'ringGroup', label: 'Ring group', description: 'Shows a ring group and joins or leaves it.', group: 'Call routing', target: 'ringGroup', short: 'Group' },
  {
    value: 'timeRule',
    label: 'Day/night switch',
    description: 'Switches a time of day rule by hand (open, closed). Lights up while it’s switched.',
    group: 'Call routing',
    target: 'timeRule',
    short: 'Night'
  },
  { value: 'menu', label: 'Auto attendant', description: 'Calls an auto attendant menu.', group: 'Call routing', target: 'autoAttendant', short: 'Menu' },
  { value: 'dnd', label: 'Do not disturb', description: 'Stops calls ringing this phone.', group: 'Phone', short: 'DND' },
  { value: 'forward', label: 'Call forwarding', description: 'Turns call forwarding on and off.', group: 'Phone', target: 'optionalNumber', short: 'Forward' },
  { value: 'conference', label: 'Conference', description: 'Brings calls together.', group: 'Phone', short: 'Conf' },
  { value: 'record', label: 'Record', description: 'Records the call.', group: 'Phone', short: 'Record' },
  { value: 'url', label: 'Web link', description: 'Opens a web address on the phone.', group: 'Advanced', target: 'url', short: 'Link' },
  { value: 'dtmf', label: 'Send digits', description: 'Sends touch tones during a call.', group: 'Advanced', target: 'digits', short: 'Digits' },
  { value: 'blank', label: 'Blank', description: 'Does nothing.', group: 'Advanced', short: '' }
];

const KEY_TYPE = new Map(KEY_TYPES.map((t) => [t.value, t]));
export function keyType(value: string): KeyType | undefined {
  return KEY_TYPE.get(value);
}

/**
 * In a change to a phone's keys, a key put back to the phone's default: { key: "L5", type:
 * "default" }. Never stored: a phone's keys and templates list only the keys that are set.
 */
export const KEY_DEFAULT = 'default';

/**
 * One programmed key. `key` is "L3" (line key 3, counting across pages) or "E1-5" (expansion
 * module 1, key 5). Keys not listed do what the phone does by default.
 * `type` "other" is something set in the phone system that Stitch doesn't model: `raw` keeps it.
 */
export interface PhoneKey {
  key: string;
  type: string;
  target?: string;
  label?: string;
  raw?: string;
}

const KEY_ID = /^(?:L([1-9][0-9]{0,2})|E([1-6])-([1-9][0-9]?))$/;
const DIAL = /^[0-9*#+][0-9*#]{0,31}$/;
const MAX_LABEL = 20;

export function keyOrder(key: string): number {
  const m = KEY_ID.exec(key);
  if (!m) return Number.MAX_SAFE_INTEGER;
  return m[1] ? Number(m[1]) : 1000 * Number(m[2]) + Number(m[3]);
}

/** Lenient: anything unreadable becomes an empty layout (validation is separate). */
export function parseKeys(value: string): PhoneKey[] {
  if (!value.trim()) return [];
  try {
    const list = JSON.parse(value) as unknown;
    if (!Array.isArray(list)) return [];
    return list
      .filter((k): k is Record<string, unknown> => typeof k === 'object' && k !== null)
      .map((k) => ({
        key: String(k.key ?? ''),
        type: String(k.type ?? ''),
        ...(typeof k.target === 'string' && k.target ? { target: k.target } : {}),
        ...(typeof k.label === 'string' && k.label ? { label: k.label } : {}),
        ...(typeof k.raw === 'string' && k.raw ? { raw: k.raw } : {})
      }));
  } catch {
    return [];
  }
}

/** The stored form: one JSON list in key order, without keys left to the phone's default. */
export function formatKeys(keys: readonly PhoneKey[]): string {
  const list = keys
    .filter((k) => k.key && k.type && k.type !== 'auto' && k.type !== KEY_DEFAULT)
    .map((k) => ({
      key: k.key,
      type: k.type,
      ...(k.target?.trim() ? { target: k.target.trim() } : {}),
      ...(k.label?.trim() ? { label: k.label.trim() } : {}),
      ...(k.raw ? { raw: k.raw } : {})
    }))
    .sort((a, b) => keyOrder(a.key) - keyOrder(b.key));
  return list.length ? JSON.stringify(list) : '';
}

/**
 * A change to a phone's keys, from what the editor started with to what it shows now: only the
 * keys that differ, each as it should be, or `default` for one put back to the phone's default.
 * Keys it doesn't list stay exactly as the phone system has them when the change is made, so a
 * screen that doesn't know a key (or a key set by someone else since) can never clear it.
 */
export function keysChange(before: readonly PhoneKey[], after: readonly PhoneKey[]): string {
  const was = new Map(parseKeys(formatKeys(before)).map((k) => [k.key, JSON.stringify(k)]));
  const now = parseKeys(formatKeys(after));
  const set = now.filter((k) => was.get(k.key) !== JSON.stringify(k));
  const keep = new Set(now.map((k) => k.key));
  const cleared = [...was.keys()].filter((key) => !keep.has(key)).map((key) => ({ key, type: KEY_DEFAULT }));
  const list = [...set, ...cleared].sort((a, b) => keyOrder(a.key) - keyOrder(b.key));
  return list.length ? JSON.stringify(list) : '';
}

/**
 * A change applied to the keys a phone has now (both as stored): keys the change lists are set,
 * or put back to the phone's default (`default`); every key it doesn't list stays as it is.
 * Used for every change to an existing phone's keys, whoever asks (the portal, a template,
 * Leland), so nothing clears a key it didn't name.
 */
export function applyKeyChange(current: string, change: string): string {
  const next = new Map(parseKeys(current).map((k) => [k.key, k]));
  for (const k of parseKeys(change)) {
    if (!k.key) continue;
    if (k.type === KEY_DEFAULT || k.type === 'auto') next.delete(k.key);
    else next.set(k.key, k);
  }
  return formatKeys([...next.values()]);
}

/** "84 keys and 3 expansion modules": what the phone system lists for a phone, in words. */
function listedInWords(listed: ReadonlySet<string>): string {
  const lines = [...listed].filter((k) => k.startsWith('L')).length;
  const modules = new Set([...listed].map((k) => /^E(\d)-/.exec(k)?.[1]).filter(Boolean)).size;
  const keys = `${lines} key${lines === 1 ? '' : 's'}`;
  return modules ? `${keys} and ${modules} expansion module${modules === 1 ? '' : 's'}` : keys;
}

/**
 * Plain-language problem with a layout, or null. `listed` is every key the phone system lists for
 * this phone (its button list, set or not), when Stitch has read it: then those are the keys that
 * exist, whatever the model's datasheet says. Without it, the model decides (a phone not yet on
 * the phone system, a template).
 */
export function checkKeys(value: string, model?: PhoneModel, listed?: readonly string[]): string | null {
  if (!value.trim()) return null;
  let list: unknown;
  try {
    list = JSON.parse(value);
  } catch {
    return 'The key layout couldn’t be read';
  }
  if (!Array.isArray(list)) return 'The key layout couldn’t be read';
  if (list.length > 400) return 'Too many keys';
  const seen = new Set<string>();
  const known = listed?.length ? new Set(listed) : null;
  for (const k of parseKeys(value)) {
    const m = KEY_ID.exec(k.key);
    if (!m) return `"${k.key}" isn’t a key on a phone`;
    if (seen.has(k.key)) return `${keyName(k.key)} is set twice`;
    seen.add(k.key);
    // Putting a key back to the phone's default: nothing to check (no such key: nothing happens).
    if (k.type === KEY_DEFAULT) continue;
    if (known) {
      if (!known.has(k.key)) return `${keyName(k.key)} isn’t on this phone (the phone system lists ${listedInWords(known)} for it)`;
    } else if (model) {
      if (m[1] && Number(m[1]) > model.lineKeys) return `${keyName(k.key)} isn’t on a ${model.vendor} ${model.model} (it has ${model.lineKeys} keys)`;
      if (m[2] && (!model.expansion || Number(m[2]) > model.expansion.max || Number(m[3]) > model.expansion.perPage * model.expansion.pages)) {
        return `${keyName(k.key)} isn’t on this phone’s expansion modules`;
      }
    }
    if (k.type === 'other') continue;
    const t = keyType(k.type);
    if (!t) return `${keyName(k.key)}: "${k.type}" isn’t something a key can do`;
    if ((k.label ?? '').length > MAX_LABEL) return `${keyName(k.key)}: labels are at most ${MAX_LABEL} characters`;
    const target = (k.target ?? '').trim();
    switch (t.target) {
      case undefined:
        if (target) return `${keyName(k.key)}: ${t.label} doesn’t take a number`;
        break;
      case 'optionalNumber':
        if (target && !DIAL.test(target)) return `${keyName(k.key)}: enter an extension or phone number`;
        break;
      case 'url':
        if (!/^https?:\/\/[^\s"'<>]{3,300}$/.test(target)) return `${keyName(k.key)}: enter a web address starting with https://`;
        break;
      case 'digits':
        if (!/^[0-9*#]{1,32}$/.test(target)) return `${keyName(k.key)}: enter the digits to send`;
        break;
      default:
        if (!target) return `${keyName(k.key)}: choose ${targetNoun(t.target)}`;
        if (!DIAL.test(target)) return `${keyName(k.key)}: enter an extension or phone number`;
    }
  }
  return null;
}

export function targetNoun(t: KeyTarget): string {
  return {
    person: 'who it’s for',
    number: 'a number',
    callPark: 'a park orbit',
    callQueue: 'a call queue',
    ringGroup: 'a ring group',
    timeRule: 'a time of day rule',
    autoAttendant: 'an auto attendant',
    optionalNumber: 'a number',
    url: 'a web address',
    digits: 'the digits'
  }[t];
}

/** "Key 3", "Expansion 1, key 5". */
export function keyName(key: string): string {
  const m = KEY_ID.exec(key);
  if (!m) return key;
  return m[1] ? `Key ${m[1]}` : `Expansion ${m[2]}, key ${m[3]}`;
}

/** Where a line key sits: its page, side and row (for drawing). */
export interface KeySlot {
  key: string;
  page: number;
  side: 'left' | 'right' | 'screen';
  row: number;
}

/**
 * How many line keys to draw for a phone: as many as the phone system lists for it when that is
 * known (its button list is the truth), else the model's; never fewer than the highest key set,
 * so a key that exists is always on the picture and never silently left out of what's saved.
 */
export function lineKeyCount(m: PhoneModel, keys: readonly PhoneKey[] = [], listed?: readonly string[]): number {
  const highest = (ids: readonly string[]) => ids.reduce((n, k) => Math.max(n, Number(/^L(\d+)$/.exec(k)?.[1] ?? 0)), 0);
  const fromList = listed?.length ? highest(listed) : 0;
  return Math.max(fromList || m.lineKeys, highest(keys.map((k) => k.key)));
}

/**
 * The programmable keys of a model, page by page, as drawn. On T3/T4/T5 phones with more than one
 * page, the last key of each page turns the page and isn't programmable, so a page holds one fewer.
 * `count` (default: the model's) draws as many keys as the phone really has, adding pages of the
 * model's size as needed.
 */
export function lineKeySlots(m: PhoneModel, count = m.lineKeys): { pages: KeySlot[][]; pageKey: boolean } {
  const pages: KeySlot[][] = [];
  if (count <= 0) return { pages: [], pageKey: false };
  if (m.keys.touch) {
    const per = Math.max(1, m.lineKeys > 0 ? Math.ceil(m.lineKeys / m.pages) : m.keys.onScreen);
    for (let first = 1, p = 0; first <= count; first += per, p++) {
      const slots: KeySlot[] = [];
      for (let i = 0; i < per && first + i <= count; i++) slots.push({ key: `L${first + i}`, page: p + 1, side: 'screen', row: i });
      pages.push(slots);
    }
    return { pages, pageKey: false };
  }
  const physical = m.keys.left + m.keys.right;
  if (physical <= 0) return { pages: [], pageKey: false };
  const paging = m.pageKey && (m.pages > 1 || count > physical) && physical > 1;
  const per = paging ? physical - 1 : physical;
  let n = 1;
  for (let p = 0; n <= count; p++) {
    const slots: KeySlot[] = [];
    for (let i = 0; i < per && n <= count; i++, n++) {
      const left = i < m.keys.left;
      slots.push({ key: `L${n}`, page: p + 1, side: left ? 'left' : 'right', row: left ? i : i - m.keys.left });
    }
    pages.push(slots);
  }
  return { pages, pageKey: paging };
}

/** A sensible start for a new phone: the first key is the person's line. */
export function defaultKeys(m: PhoneModel | undefined): PhoneKey[] {
  return m && m.lineKeys > 0 ? [{ key: 'L1', type: 'line' }] : [];
}

/** How a key reads on the phone: its label, or the name of what it points at, or its kind. */
export function keyText(k: PhoneKey, nameOf: (n: string) => string | undefined): string {
  if (k.label) return k.label;
  if (k.type === 'other') return 'Set elsewhere';
  const t = keyType(k.type);
  if (!t) return '';
  if (k.target) {
    const name = nameOf(k.target);
    if (
      name &&
      (t.target === 'person' || t.target === 'callPark' || t.target === 'callQueue' || t.target === 'ringGroup' || t.target === 'timeRule' || t.target === 'autoAttendant')
    ) {
      return name;
    }
    if (t.target === 'number' || t.target === 'optionalNumber') return `${t.short} ${k.target}`;
  }
  return t.short;
}
